'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CalcBookmarkListEntry, CalcHistoryListEntry } from '@/app/app-api/calc-params/_lib';

const BASE = '/app-api/calc-params';

const historyKey = ['calc-params', 'history'] as const;
const bookmarksKey = ['calc-params', 'bookmarks'] as const;

class HttpError extends Error {
    constructor(
        message: string,
        public status: number,
        public body: any
    ) {
        super(message);
    }
}

async function jsonFetch<T>(input: string, init?: RequestInit): Promise<T> {
    const res = await fetch(input, {
        ...init,
        headers: {
            ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
            ...init?.headers,
        },
    });
    if (!res.ok) {
        let message = `Request failed: ${res.status}`;
        let body: any = null;
        try {
            body = await res.json();
            if (body?.error) message = body.error;
        } catch {
            /* keep default */
        }
        throw new HttpError(message, res.status, body);
    }
    return res.json() as Promise<T>;
}

type HistoryListResponse = { entries: CalcHistoryListEntry[] };
type BookmarkListResponse = { entries: CalcBookmarkListEntry[] };
type HistoryEntryResponse = { entry: CalcHistoryListEntry | null };
type BookmarkEntryResponse = { entry: CalcBookmarkListEntry };
type OkResponse = { ok: true };

export function useBookmarks(options: { enabled?: boolean } = {}) {
    return useQuery({
        queryKey: bookmarksKey,
        queryFn: () => jsonFetch<BookmarkListResponse>(`${BASE}?type=bookmarks`).then((r) => r.entries),
        enabled: options.enabled ?? true,
        staleTime: 30_000,
    });
}

export function useHistory(options: { enabled?: boolean } = {}) {
    return useQuery({
        queryKey: historyKey,
        queryFn: () => jsonFetch<HistoryListResponse>(`${BASE}?type=history`).then((r) => r.entries),
        enabled: options.enabled ?? true,
        staleTime: 30_000,
    });
}

/**
 * Record a calculator run. Append-only - the same calc run twice produces
 * two history rows. Invalidates the history list.
 */
export function useRecordCalcRun() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (parameters: unknown) =>
            jsonFetch<HistoryEntryResponse>(BASE, {
                method: 'POST',
                body: JSON.stringify({ parameters }),
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: historyKey });
        },
    });
}

export type CreateBookmarkVars = { parameters: unknown; name: string };

/**
 * Create a bookmark from a parameters payload. Surfaces the 409 duplicate /
 * cap responses as `HttpError` so the UI can react (e.g. open the rename
 * dialog for the existing bookmark, or show "limit reached").
 */
export function useCreateBookmark() {
    const qc = useQueryClient();
    return useMutation<BookmarkEntryResponse, HttpError, CreateBookmarkVars>({
        mutationFn: ({ parameters, name }) =>
            jsonFetch<BookmarkEntryResponse>(`${BASE}/bookmarks`, {
                method: 'POST',
                body: JSON.stringify({ parameters, name }),
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: bookmarksKey });
        },
    });
}

export type RenameBookmarkVars = { id: number; name: string };

type BookmarkMutationContext = { previous: CalcBookmarkListEntry[] | undefined };

export function useRenameBookmark() {
    const qc = useQueryClient();
    return useMutation<BookmarkEntryResponse, HttpError, RenameBookmarkVars, BookmarkMutationContext>({
        mutationFn: ({ id, name }) =>
            jsonFetch<BookmarkEntryResponse>(`${BASE}/bookmarks/${id}`, {
                method: 'PATCH',
                body: JSON.stringify({ name }),
            }),
        onMutate: async ({ id, name }) => {
            await qc.cancelQueries({ queryKey: bookmarksKey });
            const previous = qc.getQueryData<CalcBookmarkListEntry[]>(bookmarksKey);
            qc.setQueryData<CalcBookmarkListEntry[]>(bookmarksKey, (entries) =>
                entries?.map((e) => (e.id === id ? { ...e, name } : e))
            );
            return { previous };
        },
        onError: (_err, _vars, ctx) => {
            if (ctx?.previous) qc.setQueryData(bookmarksKey, ctx.previous);
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: bookmarksKey });
        },
    });
}

export function useDeleteBookmark() {
    const qc = useQueryClient();
    return useMutation<OkResponse, HttpError, number, BookmarkMutationContext>({
        mutationFn: (id) => jsonFetch<OkResponse>(`${BASE}/bookmarks/${id}`, { method: 'DELETE' }),
        onMutate: async (id) => {
            await qc.cancelQueries({ queryKey: bookmarksKey });
            const previous = qc.getQueryData<CalcBookmarkListEntry[]>(bookmarksKey);
            qc.setQueryData<CalcBookmarkListEntry[]>(bookmarksKey, (entries) => entries?.filter((e) => e.id !== id));
            return { previous };
        },
        onError: (_err, _vars, ctx) => {
            if (ctx?.previous) qc.setQueryData(bookmarksKey, ctx.previous);
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: bookmarksKey });
        },
    });
}

export { HttpError };
